import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Button, Box, Typography, 
  FormControlLabel, Switch,
  DialogContentText,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { ptBR } from 'date-fns/locale';
import { useFormError } from '../../../components/Datatable/FormErrorProvider';

export default function CategoryForm({ open, category, onClose, onSubmit }) {
  const error = useFormError();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
    expired: null
  });

  // Carregar dados da categoria ao abrir o formulário
  useEffect(() => {
    console.log("CategoryForm useEffect", open, category);
    if (open && category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        active:  category.active,
        expired: category.expired ? new Date(category.expired) : null
      });
    } else if (open) {
      // Reset para novo formulário
      setFormData({
        name: '',
        description: '',
        active: true,
        expired: null
      });
    }
    
    // Limpar erros
    if (open) {
      error.setErrors([]);
    }
  }, [open, category]);

  // Manipular mudanças nos campos
  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData({
      ...formData,
      [name]: name === 'active' ? checked : value
    });
  };

  // Manipular mudanças na data
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      expired: date
    });
  };

  // Submeter o formulário
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar dados
    if (!formData.name.trim()) {
      error.setErrors([{ field: "name", message: "Nome da categoria é obrigatório" }]);
      return;
    }
    
    // Preparar dados para envio (omitir campos vazios)
    const dataToSubmit = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
    };
    
    // Só incluir active e expired se estiver editando
    if (category) {
      dataToSubmit.active = formData.active;
      dataToSubmit.expired = formData.expired;
    } else if (formData.expired) {
      // Para nova categoria, só incluir expired se foi definido
      dataToSubmit.expired = formData.expired;
    }
    
    onSubmit(dataToSubmit);
  };

  return (
    <ThemeProvider theme={createTheme({
      typography:{
          fontFamily:[
            'sans-serif',
            'Roboto',
            'Montserrat',
          ]
        },
        palette: {
          mode: 'light',
          primary: {
            main: "#e01b29",
          },
          }
    })}>
      <Dialog 
        open={open} 
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {category ? 'Editar Categoria' : 'Nova Categoria'}
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Preencha os dados da categoria de bloqueio.
              {category && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Esta categoria possui {category.domains_count || 0} domínios.
                </Typography>
              )}
            </DialogContentText>
            
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Nome da Categoria"
              fullWidth
              disabled={category && category.expired && new Date(category.expired) < new Date()}
              value={formData.name}
              onChange={handleChange}
              error={error.verify("name")}
              helperText={error.msgError("name")}
              required
              sx={{ mb: 2 }}
            />
            
            <TextField
              margin="dense"
              name="description"
              label="Descrição"
              disabled={category && category.expired && new Date(category.expired) < new Date()}
              fullWidth
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                //minDate={new Date()}
                disabled={category && category.expired && new Date(category.expired) < new Date()}
                label="Data de Expiração (opcional)"
                value={formData.expired}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "dense",
                    helperText: "Deixe em branco para não expirar",
                    sx: { mb: 2 }
                  }
                }}
              />
            </LocalizationProvider>
            
            {category && (
              <FormControlLabel
                control={
                  <Switch 
                    checked={formData.active} 
                    onChange={handleChange}
                    name="active"
                    color="primary"
                    disabled={category.expired && new Date(category.expired) < new Date()}
                  />
                }
                label={formData.active ? "Categoria Ativa" : "Categoria Inativa"}
              />
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary"
              disabled={category && category.expired && new Date(category.expired) < new Date()}
            >
              {category ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
}